import {
  Dialog,
  DialogTitle,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Button,
  Box,
  CircularProgress,
} from "@mui/material";
import React from "react";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import EditReceiptsDetails from "./EditReceiptsDetails";
import EditReceiptsLinkedFmeTable from "./EditReceiptsLinkedFmeTable";
import EditReceiptsReviewTable from "./EditReceiptsReviewTable";
import ModalSkeleton from "../helpers/components/ModalSkeleton";
import { useSelector } from "react-redux";
import { ReceiptsEndPointLogic } from "../../../../../logic/Finance/ProviderExpense/ReceiptsEndpointLogic";
import { selectUserData, selectUserToken } from "../../../../../features/userSlice";
import { ParingRateQueryID } from "../helpers/funcs/staticQID";
import {
  clearEditUnpair,
  setEditedReceiptsInitialized,
  setManualEditReceiptsPayload,
} from "../../../../../features/financeReceiptSlice";
import { useDispatch } from "react-redux";

interface EditReceiptProps {
  open: boolean;
  handleClose: () => void;
}

const reviewTableHeadings = {
  company: "Company",
  date: "Date",
  receipt_number: "Receipt Number",
  receipt_amount: "Receipt Amount",
  receipt_status: "Receipt Status",
  fme: "FME",
};

const EditReceiptsModal = ({ open, handleClose }: EditReceiptProps) => {
  const steps = ["Details", "Linked FME", "Review & Save"];
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const {
    loadingModal,
    editReceipts,
    selectedReceipt,
    manualEditReceiptsPayload,
    selectedReceiptDateRange,
    editUnpairPayload,
  } = useSelector((state: any) => state.receipts);
  const response = useSelector(selectUserData);
  const permission = response?.data?.website_permissions?.[0];
  const siteId = permission ? Object.keys(permission)[0] : "";
  const from_date = selectedReceiptDateRange[0]?.format("YYYY-MM-DD");
  const to_date = selectedReceiptDateRange[1]?.format("YYYY-MM-DD");
  const dispatch = useDispatch();
  const isDetailsStepValid = React.useMemo(() => {
    if (!editReceipts || editReceipts.length === 0) return false;

    return editReceipts.every((receipt: any) => {
      return (
        receipt.receiptNumber?.trim() &&
        receipt.receiptAmount !== "" &&
        !isNaN(Number(receipt.receiptAmount)) &&
        receipt.company &&
        receipt.currency &&
        receipt.status &&
        receipt.date
      );
    });
  }, [editReceipts]);

  const resetModalState = () => {
    dispatch(clearEditUnpair());
    setActiveStep(0);
    handleClose();
  };
  const [activeStep, setActiveStep] = React.useState(0);

  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      handleSave();
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (activeStep === steps.length - 1) {
      dispatch(clearEditUnpair());
    }

    if (activeStep > 0) {
      setActiveStep((prev) => prev - 1);
    }
  };

  const handleSave = async () => {
    if (!manualEditReceiptsPayload?.length || !selectedReceipt) return;
    setIsSubmitting(true);
    try {
      const editResponse = await ReceiptsEndPointLogic.editReceipt(
        oauthTokenHeader,
        manualEditReceiptsPayload[0],
        selectedReceipt.receipt_shared_uid
      );
      let deleteResponse = null;
      if (editUnpairPayload) {
        deleteResponse =
          await ReceiptsEndPointLogic.deleteFinancialMonthlyEarnings(
            oauthTokenHeader,
            editUnpairPayload
          );
      }
      const editOk = editResponse?.status === 200;
      const deleteOk = editUnpairPayload
        ? deleteResponse?.status === 200 || deleteResponse?.status === 204
        : true;
      if (editOk && deleteOk) {
        dispatch(setManualEditReceiptsPayload([]));
        dispatch(setEditedReceiptsInitialized(false));
        resetModalState();
        await ReceiptsEndPointLogic.getAllReceiptData(
          oauthTokenHeader,
          from_date,
          to_date
        );
        await ReceiptsEndPointLogic.getPairingRate(
          oauthTokenHeader,
          ParingRateQueryID,
          from_date,
          to_date,
          siteId
        );
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <>
            <EditReceiptsDetails />
          </>
        );

      case 1:
        return (
          <>
            <EditReceiptsLinkedFmeTable />
          </>
        );

      case 2:
        return (
          <>
            <EditReceiptsReviewTable tableHeadings={reviewTableHeadings} />
          </>
        );

      default:
        return "Unknown Step";
    }
  };
  const isLastStep = activeStep === steps.length - 1;
  return (
    <>
      <Dialog
        open={open}
        onClose={(_, reason) => {
          if (reason !== "backdropClick") {
            handleClose();
          }
        }}
        PaperProps={{ sx: { width: "1000px", maxWidth: "1000px" } }}
      >
        <Paper sx={{ padding: "16px 24px 20px 24px" }}>
          <DialogTitle sx={{ padding: "0px 0px 16px 0px" }}>
            Edit Receipt
          </DialogTitle>
          {loadingModal ? (
            <ModalSkeleton />
          ) : (
            <Box sx={{ width: "100%", mt: 2 }}>
              <Stepper
                sx={{
                  "& .MuiStepIcon-root": {
                    color: "rgba(0,0,0,0.3)", // default inactive
                  },
                  "& .MuiStepIcon-root.Mui-active": {
                    color: "secondary.main",
                  },
                  "& .MuiStepIcon-root.Mui-completed": {
                    color: "secondary.main",
                  },
                }}
                activeStep={activeStep}
              >
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>

              {/* Step Content */}
              <Box
                sx={{
                  my: 4,
                  px: 1,
                  minHeight: "150px",
                }}
              >
                {renderStepContent(activeStep)}
              </Box>

              {/* Buttons */}
            </Box>
          )}
          <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
            <Button
              disabled={activeStep === 0 || isSubmitting}
              onClick={handleBack}
              color="secondary"
              sx={{ mr: 1 }}
            >
              Back
            </Button>

            <Button
              color="secondary"
              variant="outlined"
              disabled={isSubmitting}
              onClick={resetModalState}
              sx={{ mr: 1 }}
            >
              CANCEL
            </Button>

            <Button
              key={activeStep}
              variant="contained"
              color="secondary"
              onClick={handleNext}
              {...(isLastStep ? { "data-tracker": 585 } : {})}
              disabled={
                isSubmitting ||
                loadingModal ||
                (activeStep === 0 && !isDetailsStepValid)
              }
              endIcon={
                isSubmitting ? (
                  <CircularProgress size={18} color="inherit" />
                ) : activeStep === steps.length - 1 ? (
                  <SaveOutlinedIcon sx={{ ml: "0.5" }} />
                ) : (
                  <ChevronRightIcon sx={{ ml: "0.5" }} />
                )
              }
            >
              {isSubmitting
                ? "Saving..."
                : activeStep === steps.length - 1
                ? "save changes"
                : "Next"}
            </Button>
          </Box>
        </Paper>
      </Dialog>
    </>
  );
};

export default EditReceiptsModal;
